import { useState, useCallback, useEffect, useMemo, CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs/Tabs';
import Blade from '@/components/ui/Blade/Blade';
import Button from '@/components/ui/Button';
import { ManagementChildDtoIn } from '@/models/in/ManagementChildDtoIn';
import { RelationshipStatus } from '@/models/common/RelationshipStatus';
import { DocumentStatus } from '@/models/common/DocumentStatus';
import userSupport from '@/assets/icons/user-support.svg';
import documentsIcon from '@/assets/icons/documents.svg';
import { getStatusColorSets } from '@/helpers/cssHelpers';
import StatusChangeDialog from './StatusChangeDialog';
import RequestCompletionDialog from '@/components/RequestCompletionDialog/RequestCompletionDialog';
import DownloadFileButton from '@/components/ui/DownloadFileButton/DownloadFileButton';
import styles from './AdditionalInfo.module.css';

interface AdditionalInfoProps {
  data: ManagementChildDtoIn;
  initialTab: 'child' | 'parent';
  handleXclick: () => void;
  onRefresh: () => void;
  onDataUpdate: (updatedData: ManagementChildDtoIn) => void;
  onOpenOfficeComments?: () => void;
}

const AdditionalInfo = ({
  data,
  initialTab,
  handleXclick,
  onRefresh,
  onDataUpdate,
  onOpenOfficeComments,
}: AdditionalInfoProps) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<string>(initialTab === 'parent' ? 'parentDetails' : 'childDetails');

  // Memoize status color sets from CSS variables
  const statusColorSets = useMemo(() => getStatusColorSets(), []);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedNewStatus, setSelectedNewStatus] = useState<RelationshipStatus | null>(null);
  const [completionDialogOpen, setCompletionDialogOpen] = useState(false);

  // Update active tab when initialTab changes
  useEffect(() => {
    setActiveTab(initialTab === 'parent' ? 'parentDetails' : 'childDetails');
  }, [initialTab, data]);

  const handleOpenStatusDialog = useCallback((newStatus: RelationshipStatus) => {
    setSelectedNewStatus(newStatus);
    setStatusDialogOpen(true);
  }, []);

  const handleCloseStatusDialog = useCallback(() => {
    setStatusDialogOpen(false);
    setSelectedNewStatus(null);
  }, []);

  const handleStatusChangeSuccess = useCallback((updatedData: ManagementChildDtoIn) => {
    handleCloseStatusDialog();
    onDataUpdate(updatedData);
    onRefresh();
  }, [handleCloseStatusDialog, onDataUpdate, onRefresh]);

  const formatDate = (date: string) => {
    return Intl.DateTimeFormat('he-IL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date(date));
  };

  const handleCompletionSuccess = useCallback(() => {
    setCompletionDialogOpen(false);
    onRefresh();
  }, [onRefresh]);

  const canApprove = data.status !== RelationshipStatus.Approved;
  const canReject = data.status !== RelationshipStatus.Rejected;
  const canReturnToPending = data.status !== RelationshipStatus.PendingApproval;

  const headerTop = (
    <div className="flex items-center gap-3">
      <img src={userSupport} alt="" className="w-6 h-6" />
      <span className="body-lr-regular">
        {t('childrenManagement.childDetails')}
        {' - '}
        <span className="body-lr-link">
          {data.child.firstName} {data.child.lastName}
        </span>
      </span>
    </div>
  );

  const headerBottom = (
    <div className="flex gap-2">
      <span className={`${styles.status} body-sm-medium`}>
        <span
          className={styles['status-color']}
          style={{ '--status-color': statusColorSets[data.status].border } as CSSProperties}
        ></span>
        {t(`childrenManagement.status.${data.status}`)}
      </span>
      <span className="body-sm-regular">{formatDate(data.requestDate)}</span>
    </div>
  );

  return (
    <>
      <Blade handleXclick={handleXclick} headerTop={headerTop} headerBottom={headerBottom} width="520px">
        <Tabs value={activeTab} onValueChange={setActiveTab} style={{ direction: 'inherit' }}>
          <TabsList>
            <TabsTrigger value="childDetails">{t('childrenManagement.childDetails')}</TabsTrigger>
            <TabsTrigger value="parentDetails">{t('childrenManagement.parentDetails')}</TabsTrigger>
          </TabsList>

          {/* Child Details Tab */}
          <TabsContent value="childDetails" className={`${styles.content} custom-scrollbar`}>
            <div className={styles.infoSection}>
              <h3 className="body-md-semibold text-(--font-heading)">{t('childrenManagement.childDetails')}</h3>

              <div className={styles.infoRow}>
                <span className={`body-sm-regular ${styles.infoLabel}`}>{t('fullName')}:</span>
                <span className={`body-sm-medium ${styles.infoValue}`}>
                  {data.child.firstName} {data.child.lastName}
                </span>
              </div>

              <div className={styles.infoRow}>
                <span className={`body-sm-regular ${styles.infoLabel}`}>{t('israeliId')}:</span>
                <span className={`body-sm-medium ${styles.infoValue}`}>{data.child.israeliId}</span>
              </div>

              {data.child.birthDate && (
                <div className={styles.infoRow}>
                  <span className={`body-sm-regular ${styles.infoLabel}`}>{t('birthDate')}:</span>
                  <span className={`body-sm-medium ${styles.infoValue}`}>
                    {formatDate(String(data.child.birthDate))}
                  </span>
                </div>
              )}

              <div className={styles.infoRow}>
                <span className={`body-sm-regular ${styles.infoLabel}`}>{t('childrenManagement.columns.status')}:</span>
                <span className={`body-sm-medium ${styles.infoValue}`}>
                  {t(`childrenManagement.status.${data.status}`)}
                </span>
              </div>

              <div className={styles.infoRow}>
                <span className={`body-sm-regular ${styles.infoLabel}`}>{t('livesWithSupported')}:</span>
                <span className={`body-sm-medium ${styles.infoValue}`}>{t(data.livesWithSupported ? 'yes' : 'no')}</span>
              </div>
            </div>

            <div className={styles.line} />

            <div className={styles.infoSection}>
              <h3 className="body-md-semibold text-(--font-heading)">{t('childrenManagement.requestInfo')}</h3>

              <div className={styles.infoRow}>
                <span className={`body-sm-regular ${styles.infoLabel}`}>{t('childrenManagement.requestDate')}:</span>
                <span className={`body-sm-medium ${styles.infoValue}`}>{formatDate(data.requestDate)}</span>
              </div>

              {data.requestedByEmail && (
                <div className={styles.infoRow}>
                  <span className={`body-sm-regular ${styles.infoLabel}`}>{t('childrenManagement.requestedBy')}:</span>
                  <span className={`body-sm-medium ${styles.infoValue}`}>{data.requestedByEmail}</span>
                </div>
              )}

              {data.updatedByEmail && (
                <>
                  <div className={styles.infoRow}>
                    <span className={`body-sm-regular ${styles.infoLabel}`}>{t('childrenManagement.updatedBy')}:</span>
                    <span className={`body-sm-medium ${styles.infoValue}`}>{data.updatedByEmail}</span>
                  </div>
                  {data.updatedAt && (
                    <div className={styles.infoRow}>
                      <span className={`body-sm-regular ${styles.infoLabel}`}>{t('childrenManagement.lastUpdated')}:</span>
                      <span className={`body-sm-medium ${styles.infoValue}`}>{formatDate(data.updatedAt)}</span>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className={styles.line} />

            {/* Documents Section */}
            <div className="flex flex-col gap-3">
              <h3 className="body-md-semibold text-(--font-heading)">{t('documentsAndApprovals')}</h3>
              <table className="w-full">
                <thead className={styles.thead}>
                  <tr>
                    <th className={`${styles.th} body-xs-sm-bold`}>
                      {t('document')}/{t('approval')}
                    </th>
                    <th className={`${styles.th} body-xs-sm-bold`}>{t('uploadDate')}</th>
                    <th className={`${styles.th} body-xs-sm-bold`}>{t('documentStatus')}</th>
                    <th className={`${styles.th} body-xs-sm-bold`}>{t('comment')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.documents && data.documents.length > 0 ? (
                    data.documents.map((document, index) => (
                      <tr key={`${document.name}-${index}`}>
                        <td className={`body-sm-regular ${styles.td}`}>
                          <div className="flex items-center gap-1">
                            <a target="_blank" rel="noopener noreferrer" href={document.uri} className="text-(--font-link)">
                              {document.name}
                            </a>
                            <DownloadFileButton uri={document.uri} fileName={document.name} />
                          </div>
                        </td>
                        <td className={`body-sm-regular ${styles.td}`}>
                          {formatDate(String(document.creationTime))}
                        </td>
                        <td className={`body-sm-regular ${styles.td}`}>
                          {document.status === DocumentStatus.Rejected ? (
                            <span className="text-red-600">{t('statusRejected')}</span>
                          ) : (
                            <span className="text-(--font-body-3)">—</span>
                          )}
                        </td>
                        <td className={`body-sm-regular ${styles.td}`}>
                          {document.officeComment || document.userComment || '—'}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4}>
                        <div className={styles.noDocuments}>
                          <img src={documentsIcon} alt="" />
                          <p className="body-md-regular text-(--font-body-3)">{t('childrenManagement.noDocumentsAttached')}</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {onOpenOfficeComments && (
              <>
                <div className={styles.line} />
                <div className={styles.infoSection}>
                  <div className={styles.infoRow}>
                    <span className={`body-sm-semibold ${styles.infoLabel}`} style={{ color: 'var(--font-heading)' }}>{t('officeComments.title')}:</span>
                    <button className={styles.officeCommentsButton} onClick={onOpenOfficeComments}>
                      {t('officeComments.manageComments')}
                      {(data.officeComments?.length ?? 0) > 0 && (
                        <span className={styles.officeCommentsBadge}>{data.officeComments?.length}</span>
                      )}
                    </button>
                  </div>
                </div>
              </>
            )}

            <div className={styles.line} />

            <div className={styles.infoSection}>
              <h3 className="body-md-semibold text-(--font-heading)">{t('childrenManagement.changeStatus')}</h3>
              <div className={styles.actionsRow}>
                <button
                  className={`${styles.actionButton} ${styles.approveButton}`}
                  onClick={() => handleOpenStatusDialog(RelationshipStatus.Approved)}
                  disabled={!canApprove}
                >
                  {t('childrenManagement.approveChild')}
                </button>
                <button
                  className={`${styles.actionButton} ${styles.rejectButton}`}
                  onClick={() => handleOpenStatusDialog(RelationshipStatus.Rejected)}
                  disabled={!canReject}
                >
                  {t('childrenManagement.rejectChild')}
                </button>
                <button
                  className={`${styles.actionButton} ${styles.pendingButton}`}
                  onClick={() => handleOpenStatusDialog(RelationshipStatus.PendingApproval)}
                  disabled={!canReturnToPending}
                >
                  {t('childrenManagement.returnToPending')}
                </button>
                <Button variant="outline" onClick={() => setCompletionDialogOpen(true)}>
                  {t('documents.requestDocumentCompletion')}
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Parent Details Tab */}
          <TabsContent value="parentDetails" className={`${styles.content} custom-scrollbar`}>
            <div className={styles.infoSection}>
              <h3 className="body-md-semibold text-(--font-heading)">{t('childrenManagement.parentDetails')}</h3>

              <div className={styles.infoRow}>
                <span className={`body-sm-regular ${styles.infoLabel}`}>{t('fullName')}:</span>
                <span className={`body-sm-medium ${styles.infoValue}`}>
                  {data.parent.firstName} {data.parent.lastName}
                </span>
              </div>

              <div className={styles.infoRow}>
                <span className={`body-sm-regular ${styles.infoLabel}`}>{t('israeliId')}:</span>
                <span className={`body-sm-medium ${styles.infoValue}`}>{data.parent.israeliId}</span>
              </div>

              {data.parent.phone && (
                <div className={styles.infoRow}>
                  <span className={`body-sm-regular ${styles.infoLabel}`}>{t('phoneNumber')}:</span>
                  <span className={`body-sm-medium ${styles.infoValue}`}>{data.parent.phone}</span>
                </div>
              )}

              {data.parent.email && (
                <div className={styles.infoRow}>
                  <span className={`body-sm-regular ${styles.infoLabel}`}>{t('email')}:</span>
                  <span className={`body-sm-medium ${styles.infoValue}`}>{data.parent.email}</span>
                </div>
              )}
            </div>

            <div className={styles.line} />

            <div className={styles.infoSection}>
              <h3 className="body-md-semibold text-(--font-heading)">{t('childrenManagement.parentChildrenCount')}</h3>
              <div className={styles.infoRow}>
                <span className={`body-sm-regular ${styles.infoLabel}`}>{t('childrenManagement.totalChildren')}:</span>
                <span className={`body-sm-medium ${styles.infoValue}`}>{data.parent.childrenCount}</span>
              </div>
            </div>

            <div className={styles.line} />

            <div className={styles.infoSection}>
              <h3 className="body-md-semibold text-(--font-heading)">{t('childrenManagement.parentFunds')}</h3>
              {data.parent.fundIds && data.parent.fundIds.length > 0 ? (
                <div className={styles.fundsList}>
                  {data.parent.fundIds.map((fundId) => (
                    <span key={fundId} className={`${styles.fundBadge} body-sm-medium`}>
                      {fundId}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="body-sm-regular text-(--font-body-3)">{t('childrenManagement.noFunds')}</p>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </Blade>

      {statusDialogOpen && selectedNewStatus && (
        <StatusChangeDialog
          open={true}
          onClose={handleCloseStatusDialog}
          parentId={data.parentId}
          childId={data.childId}
          newStatus={selectedNewStatus}
          onSuccess={handleStatusChangeSuccess}
        />
      )}

      {completionDialogOpen && (
        <RequestCompletionDialog
          isOpen={true}
          onClose={() => setCompletionDialogOpen(false)}
          apiEndpoint={`management/children/${data.parentId}/${data.childId}/request-completion`}
          onSuccess={handleCompletionSuccess}
          documents={data.documents}
        />
      )}
    </>
  );
};

export default AdditionalInfo;
